/**
 * SocialPulseAbout — About SocialPulse.
 *
 * Long-form corporate About page describing the mission, network model
 * and long-term vision, plus a founder card that links out to the full
 * founder story at /founder. Uses the SocialPulse brand palette
 * (emerald green, violet, blue, orange, lavender) — no dark backgrounds.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Network, Layers, Sparkles, Landmark,
  BookOpen, Users, HeartHandshake, Camera, Leaf,
} from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

export default function SocialPulseAbout() {
  useEffect(() => {
    document.title = "About SocialPulse — Connecting People, Places, Knowledge and Communities";
    setMeta("description", "SocialPulse® organizes useful information around the places where people live, work, study, travel and connect — a connected network of geographic portals and specialized platforms.");
    setLink("canonical", "https://socialpulse.com/about");
    setMeta("og:title", "About SocialPulse", "property");
    setMeta("og:description", "Connecting People, Places, Knowledge and Communities.", "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", "https://socialpulse.com/about", "property");
    // Remove any prior noindex the placeholder set.
    setMeta("robots", "index, follow");
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-about-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(140deg, #F0F6FF 0%, #F5F1FA 35%, #ECFDF5 100%)",
        }}
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-10 sm:pb-14">
          <p className="text-xs font-semibold uppercase tracking-[.22em] text-violet-700">About SocialPulse</p>
          <h1
            className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
            data-testid="sp-about-h1"
          >
            Connecting People, Places,
            <br />
            Knowledge and <span className="text-emerald-700">Communities.</span>
          </h1>
          <p className="mt-5 text-slate-700 text-base sm:text-lg leading-relaxed">
            SocialPulse® is a global digital platform designed to organize
            useful information around the places where people live, work,
            study, travel and connect.
          </p>
          <p className="mt-4 text-slate-700 text-base sm:text-lg leading-relaxed">
            Our vision is to build a connected information and community
            network covering countries, states and provinces, counties and
            districts, cities, towns, villages and local communities around
            the world.
          </p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 py-12 sm:py-16">

        <p className="text-slate-700 text-base leading-relaxed">
          Instead of treating geographic information, community participation
          and specialized services as separate destinations, SocialPulse
          brings them together through a shared network of geographic
          portals and focused platforms.
        </p>

        {/* Mission */}
        <Section
          eyebrow="Our Mission"
          title="Make useful knowledge easier to find, share and improve."
          tint="violet"
          icon={Sparkles}
        >
          <p>
            The mission of SocialPulse is simple: make useful local and global
            information easier to discover, understand, share and improve
            through community participation and responsible technology.
          </p>
          <p>
            A student researching a village, a traveler exploring a
            destination, a farmer promoting local produce, an alumnus looking
            for former classmates, a family preserving its history, or a
            resident wanting to participate in a local discussion should all
            be able to begin with a place or topic and discover the
            information and connections relevant to them.
          </p>
        </Section>

        {/* Global network */}
        <Section
          eyebrow="A Global Network Built Around Places"
          title="One geographic backbone, from the world down to a village."
          tint="emerald"
          icon={MapPin}
        >
          <p>
            SocialPulse is being designed around a common geographic structure:
          </p>
          <p className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-3 text-emerald-800 text-sm sm:text-base font-medium leading-relaxed">
            World → Country → State / Province → County / District → City /
            Town / Village → Local Community
          </p>
          <p>
            Each place can become a living information portal containing
            useful reference information such as history, geography,
            population, culture, education, agriculture, transportation,
            tourism, nature, food, health resources, businesses, community
            organizations and other locally relevant subjects.
          </p>
          <p>
            Unlike a traditional directory or encyclopedia, these portals are
            intended to evolve with their communities. Information can be
            connected with current news, photographs, discussions, events,
            local organizations, farms, food resources, travel information and
            contributions from people who know the community.
          </p>
        </Section>

        {/* Many specialized platforms */}
        <Section
          eyebrow="One Network, Many Specialized Platforms"
          title="An ecosystem — not a single website."
          tint="blue"
          icon={Network}
        >
          <p>
            SocialPulse is being developed as an ecosystem rather than a
            single website. Specialized platforms can concentrate on subjects
            such as geographic information, travel, agriculture, food access,
            nature, alumni communities, family history, photographs, community
            discussions, classifieds and other areas while sharing a common
            geographic and community infrastructure.
          </p>
          <p>
            The goal is to avoid creating isolated information silos. A farm,
            school, photograph, discussion, event, business or nature
            observation associated with a particular community can potentially
            be discovered from that community's geographic portal as well as
            from the specialized platform where the information originated.
          </p>
          <p className="italic text-slate-800 font-semibold">
            Create once. Connect by place and subject. Discover across the
            network.
          </p>
          <div className="pt-2">
            <Link
              to="/network"
              data-testid="sp-about-network-cta"
              className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 text-sm font-semibold"
            >
              See every site in the SocialPulse network <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Section>

        {/* Local + global */}
        <Section
          eyebrow="Local Knowledge With Global Reach"
          title="Residents know their neighborhoods — we help them share."
          tint="orange"
          icon={BookOpen}
        >
          <p>
            Much of the world's most useful information is local. Residents
            know their neighborhoods. Farmers understand their land and crops.
            Teachers and students know their schools. Families preserve
            histories that may never appear in conventional reference sources.
            Travelers document changing destinations. Local organizations
            understand the needs and resources of their communities.
          </p>
          <p>
            SocialPulse seeks to provide an organized way for this knowledge
            to complement reliable public information and authoritative data.
            Community contributions should not replace trustworthy sources —
            the platform is being designed to distinguish sourced reference
            information from community-created content and to provide
            reporting, correction and moderation mechanisms.
          </p>
        </Section>

        {/* Technology */}
        <Section
          eyebrow="Technology With a Human Purpose"
          title="AI as an enabler, not a substitute."
          tint="lavender"
          icon={Layers}
        >
          <p>
            Artificial intelligence can make an enormous amount of information
            easier to organize, translate, search and understand. SocialPulse
            intends to use AI as an enabling technology — not as a substitute
            for people or communities.
          </p>
          <p>
            Potential capabilities across the network include multilingual
            content, text-to-speech, intelligent search and discovery,
            assistance with organizing public information, accessibility
            improvements, content connections, recommendations and tools that
            help communities contribute useful knowledge. The objective is
            technology that reduces barriers to information and participation.
          </p>
        </Section>

        {/* Preservation */}
        <Section
          eyebrow="Preserving Local History"
          title="Communities change. We help capture what mattered."
          tint="rose"
          icon={Camera}
        >
          <p>
            Communities change constantly. Buildings disappear. Farms close.
            Plant and animal habitats change. Schools merge. Businesses move.
            Traditions fade. Families migrate. Photographs and personal
            histories can be lost within a generation.
          </p>
          <p>
            SocialPulse and its related platforms seek to help communities
            document some of this changing history while also providing
            useful information about the present. A photograph, family
            record, alumni memory, farm profile or nature observation created
            today may become an important historical record many years from
            now.
          </p>
        </Section>

        {/* Designed for everyone */}
        <Section
          eyebrow="Designed for Everyone"
          title="A platform for students, residents, families and more."
          tint="teal"
          icon={Users}
        >
          <p>
            SocialPulse is intended to serve a broad community, including
            students and teachers; residents and community groups; travelers;
            farmers and food producers; local businesses and organizations;
            alumni and schools; families; researchers; photographers and
            nature observers; nonprofit organizations; public institutions;
            and people interested in learning about communities beyond their
            own.
          </p>
          <p>
            Accessibility, multilingual communication and text-to-speech are
            therefore important parts of the long-term platform architecture.
          </p>
        </Section>

        {/* Social capital */}
        <Section
          eyebrow="Community Participation and Social Capital"
          title="Healthy communities depend on people helping people."
          tint="emerald"
          icon={HeartHandshake}
        >
          <p>
            Healthy communities depend on more than information. They depend
            on people helping one another. SocialPulse encourages constructive
            participation through volunteering, sharing useful knowledge,
            documenting communities, helping local organizations, supporting
            farmers and businesses, participating in discussions and
            contributing information that benefits others.
          </p>
          <p>
            The broader SocialPulse ecosystem is also exploring ways to
            recognize this positive participation through the concept of
            <strong> Social Capital™</strong> — the value created when people
            contribute their knowledge, time and effort for the benefit of
            their communities.
          </p>
        </Section>

        {/* Grow locally */}
        <Section
          eyebrow="A Platform That Can Grow Locally"
          title="Global infrastructure, local operators."
          tint="blue"
          icon={Landmark}
        >
          <p>
            No central organization can document every community in the world
            by itself. SocialPulse is therefore being designed so that
            geographic portals can eventually involve local contributors,
            organizations and responsible local operators while remaining
            connected to shared platform standards and infrastructure.
          </p>
          <p>
            This approach can allow a village or city portal to become
            genuinely local without becoming disconnected from the larger
            global network.
          </p>
        </Section>

        {/* Looking ahead */}
        <Section
          eyebrow="Looking Ahead"
          title="Building connections — not just websites."
          tint="lavender"
          icon={Leaf}
        >
          <p>
            The long-term vision is ambitious: create an interconnected
            digital resource where virtually any place can have a useful,
            evolving presence and where information from specialized platforms
            can be connected naturally through geography.
          </p>
          <p>
            SocialPulse is still evolving. New geographic portals, community
            capabilities, specialized platforms and partnerships will continue
            to be developed as the network grows.
          </p>
          <p>
            The objective is not simply to build more websites. It is to
            build connections — between information and people, between
            local communities and the wider world, and between today's
            knowledge and tomorrow's history.
          </p>
        </Section>

        {/* Founder card */}
        <section
          className="mt-14 rounded-2xl ring-1 ring-violet-200 p-6 sm:p-8"
          style={{
            background:
              "linear-gradient(135deg, #F5F1FA 0%, #EEF2FF 45%, #ECFDF5 100%)",
          }}
          data-testid="sp-about-founder-card"
        >
          <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6">
            <div
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden ring-4 ring-white shadow-md shrink-0"
              data-testid="sp-about-founder-portrait"
            >
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/8nkbgsua_IMG_0689.jpeg"
                alt="Thomas George Vellaringattu"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[.22em] text-violet-700">About the Founder</p>
              <h2 className="mt-2 font-serif font-bold text-slate-900 text-2xl sm:text-3xl leading-tight">
                Thomas George Vellaringattu
              </h2>
              <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">
                SocialPulse was conceived and founded by Thomas George
                Vellaringattu, whose career has crossed hospitality,
                entrepreneurship, computer programming, data technology and
                community-oriented digital projects. His experiences living and
                working in India, the Middle East and the United States helped
                shape the idea of creating technology that connects local
                knowledge with a global information network.
              </p>
              <div className="mt-5">
                <Link
                  to="/founder"
                  data-testid="sp-about-founder-link"
                  className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
                >
                  Read the Founder Story <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="mt-14 flex flex-wrap gap-3">
          <Link
            to="/network"
            data-testid="sp-about-cta-network"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
          >
            Explore the network <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            data-testid="sp-about-cta-contact"
            className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors"
          >
            Contact us
          </Link>
        </div>

        <p className="mt-14 text-xs text-slate-500">
          Publisher: SocialPulse Inc.
        </p>
      </main>

      <SocialPulseFooter />
    </div>
  );
}

/* ─────────────────── UI atoms ─────────────────── */

const TINTS = {
  violet:   { bg: "bg-violet-50",   text: "text-violet-700",   ring: "ring-violet-200" },
  emerald:  { bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-200" },
  blue:     { bg: "bg-blue-50",     text: "text-blue-700",     ring: "ring-blue-200" },
  orange:   { bg: "bg-orange-50",   text: "text-orange-700",   ring: "ring-orange-200" },
  lavender: { bg: "bg-indigo-50",   text: "text-indigo-700",   ring: "ring-indigo-200" },
  rose:     { bg: "bg-rose-50",     text: "text-rose-700",     ring: "ring-rose-200" },
  teal:     { bg: "bg-teal-50",     text: "text-teal-700",     ring: "ring-teal-200" },
};

function Section({ eyebrow, title, tint, icon: Icon, children }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-9 h-9 grid place-items-center rounded-xl ${t.bg} ${t.text} ring-1 ${t.ring}`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <p className={`text-xs font-semibold uppercase tracking-[.22em] ${t.text}`}>
          {eyebrow}
        </p>
      </div>
      <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl leading-tight">
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-slate-700 text-[15px] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
